'use client';

import { ActionButtonWithDescription } from '@/shared/components/action-button-with-description';
import { useRouter } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import { cn } from '@ecom/helper-react-class-combine';
import { GameData } from '@/features/games/shared/types/game-data';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { getIpcClient } from '@/features/games-app/shared/client/ipc-client';
import {
  ResponseOnlineProductShortcut,
  ResponseLaunchProduct,
  ResponsePlayOnlineProduct,
} from '@/features/games-app/shared/types/ipc.types';
import { ExternalPlayIcon } from '@/shared/components/primitives/svg/external-play-icon';

export const PlayWebGameButton = ({
  gameData,
  className,
}: {
  gameData: GameData;
  className?: string;
}) => {
  const t = useTranslations('GamePage');
  const isGamesApp = useIsWTGamesApp();

  const router = useRouter();

  const title = isGamesApp ? (
    <span className='flex items-center gap-1'>
      <ExternalPlayIcon /> {t('play')}
    </span>
  ) : (
    t('play')
  );

  const description = isGamesApp ? t('launchGameInWebBrowser') : t('stableConnectionRequired');

  const handleOpenLink = async (): Promise<void> => {
    if (isGamesApp) {
      const ipcClient = getIpcClient();
      const gameUrl = `${window.location.origin}${gameData.path}play`;

      await ipcClient.send<ResponseOnlineProductShortcut>({
        name: 'productcatalog',
        action: 'createonlineproductshortcut',
        body: {
          productId: gameData.sku,
          productName: gameData.name,
          productUrl: gameUrl,
          createStartMenuShortcut: true,
          createDesktopShortcut: true,
        },
      });

      await ipcClient.send<ResponsePlayOnlineProduct>({
        name: 'productcatalog',
        action: 'playonlineproduct',
        body: {
          productId: gameData.sku,
          launch: false,
        },
      });

      await ipcClient.send<ResponseLaunchProduct>({
        name: 'environment',
        action: 'launchexternalbrowser',
        body: { externalUrl: gameUrl },
      });
      return;
    }
    router.push(`${gameData.path}play`);
  };

  return (
    <>
      <ActionButtonWithDescription
        title={<span className='text-lg'>{title}</span>}
        description={description}
        variant='primary'
        onClick={handleOpenLink}
        className={cn('min-h-20', className)}
      />
    </>
  );
};
